"use client";

import ImageWrapper from "@/components/ImageWrapper";
import Loader from "@/components/Loader";
import { addDataLayer } from "@/helpers/dataLayer";
import {
  checkSceneCard,
  getLinkedInfo,
  shouldShowPopup,
  triggerVerifyCard,
} from "@/helpers/sceneCardFunctions";
import { clearShoppingSlice } from "@/helpers/shoppingListHelper";
import { useLogin } from "@/helpers/useCommonFunctions";
import CreateAccountPopup from "@/templates/gigya/Sceneplus/createAccountPopup";
import CreateScenePopup from "@/templates/gigya/Sceneplus/createSceneAccountOffer";
import AddSceneCardPopup from "@/templates/gigya/Sceneplus/linkSceneCardPopup";
import VerifyPopup from "@/templates/gigya/Sceneplus/verifyPopup";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { resetOffersFromStorage } from "@/utils/redux/favOffers";
import { setLoginParams, setRenderMemo } from "@/utils/redux/gigya";
import { sceneStatesTrigger, setAlert } from "@/utils/redux/gigya/scene";
import { useGigya, useGigyaLogout } from "@dxp-feed/gigya";
import {
  Box,
  DropdownMenu,
  DropdownTrigger,
  Toast,
  Text,
  DropdownContent,
  Button,
  Badge,
} from "@dxp-feed/ui";
import { deleteCookie, getCookie } from "cookies-next";
import Link from "@/components/Link";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import React from "react";
// import DeleteAccountPopup from "./deleteAccountPopup";
import { setLogoutUser } from "@/utils/redux/gigya/logout";
import { setGhostCard } from "@/utils/redux/gigya/ghostCard";
import { enquireEnrolment, setCustomgigyaUid } from "@/services/newRelic";
import { generateNonce } from "@/helpers/noncehelper";
import { flushToken, useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { encrypt } from "@/utils/encrypt";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { REVERSE_CARD_STATUS } from "@/utils/constant";
import { RenderHTML } from "@/helpers/ServerFuncytions";

export default function UserInfo() {
  const router = useRouter();
  const { loadLogin } = useLogin(); // Directly pass the flow string
  const { gigya, isLoaded } = useGigya();
  const pathName = usePathname();
  const dispatch = useAppDispatch();
  // const [deleteDialog, setDeleteDialog] = useState(false);

  function formatName(name: string) {
    const firstName = name?.slice(0, 5) || "";
    return `Hi, ${name?.length > 5 ? `${firstName}...` : name}`;
  }
  const searchParams = useSearchParams();
  const isHash = [...searchParams.keys()].includes("hash");

  const [loader, setLoading] = useState(false);
  const { GigyaLogout } = useGigyaLogout({ gigya, isLoaded });
  const [isOpen, setIsOpen] = useState(true);
  const logoutUser = useAppSelector((state) => state.logoutSlice.logoutUser);
  const ghostCardHandler = useAppSelector(
    (state) => state.ghostCardSlice.ghostCard
  );

  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );

  useEffect(() => {
    if (logoutUser) {
      handleGigyaLogout();
      dispatch(setLogoutUser(false));
    }
    if (ghostCardHandler) {
      handleProfileUpdate({ isProfileUpdate: true });
      dispatch(setGhostCard(false));
    }
  }, [logoutUser, dispatch, ghostCardHandler]);
  const [close, setClose] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: TouchEvent) {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setClose(false);
      }
    }
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleGigyaLogout = async () => {
    const optanonConsent = await getCookie("OptanonConsent");
    const optanonAlert = await getCookie("OptanonAlertBoxClosed");
    dispatch(resetOffersFromStorage([]));
    const logoutUser = (response: {
      errorCode: number;
      errorMessage: string;
      UID: string;
    }) => {
      if (response.errorCode === 0) {
        try {
          dispatch(clearShoppingSlice());
        } catch (error) {
          console.log("Failed to clear redux.");
        }
        addDataLayer({
          event: "gigyaUID",
          gigyaID: response.UID,
        });
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        if (optanonConsent) {
          document.cookie = `OptanonConsent=${optanonConsent}; path=/; expires=${oneYearFromNow.toUTCString()};`;
        }
        if (optanonAlert) {
          document.cookie = `OptanonAlertBoxClosed=${optanonAlert}; path=/; expires=${oneYearFromNow.toUTCString()};`;
        }
        if (typeof window != "undefined") {
          window.location.href = "/";
        }
      }
    };
    // Check if gigya is available and loaded
    if (isLoaded && gigya) {
      try {
        addDataLayer({
          event: "sign_out",
          click_text: "sign_out",
        });
        GigyaLogout(logoutUser);
      } catch (error) {
        console.log(error);
      }
    }
  };
  const userSession = useAppSelector((state) => state.gigyaslice);
  const { alert } = useAppSelector((state) => state.sceneSlice);

  const { suppression, popupType } = useAppSelector(
    (state) => state.sceneSlice
  );

  const oneTrustFunction = async ({
    loggedIn,
    uid,
  }: {
    loggedIn: boolean;
    uid: string | null;
  }) => {
    let token = "";
    // Only fetch token if the user is logged in
    if (loggedIn) {
      const tokenResponse = await getGigyaToken();
      const response = await fetch("/api/generateToken", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-nonce-token": generateNonce(),
          Authorization: `Bearer ${encrypt(crypto.randomUUID()).toUpperCase()}`,
          focus: tokenResponse.idToken,
        },
      });
      const data = await response.json();
      token = data.token;
    }

    // If OneTrust is already loaded, just update the dataSubjectParams
    if (
      typeof (window as any).OneTrust !== "undefined" &&
      (window as any).OneTrust.dataSubjectParams
    ) {
      (window as any).OneTrust.dataSubjectParams = {
        id: uid || "",
        isAnonymous: !uid,
        token,
      };
      return;
    }

    // If OneTrust isn't already loaded, inject the variable script
    const existingVariableScript = document.getElementById(
      "one-trust-variables"
    );
    if (existingVariableScript) {
      existingVariableScript.remove();
    }

    const scriptOneTrustVariables = document.createElement("script");
    scriptOneTrustVariables.type = "text/javascript";
    scriptOneTrustVariables.id = "one-trust-variables";
    scriptOneTrustVariables.innerHTML = `
      var OneTrust = {
        dataSubjectParams: {
          id: "${uid || ""}",
          isAnonymous: ${uid ? "false" : "true"},
          token: "${token}",
        },
      };
    `;
    document.head.appendChild(scriptOneTrustVariables);

    // Inject OneTrust SDK only if it's not already present
    let existingSDKScript = null;
    try {
      existingSDKScript = document?.querySelector("#one-trust-script");
    } catch (err) {
      console.error("something went wrong", err);
    }
    // const existingSDKBlockScript = document.querySelector("#one-trust-block");
    // if (!existingSDKBlockScript) {
  //   const scriptOneTrustSDK = document.createElement("script");
    //   scriptOneTrustSDK.src = `${process.env.NEXT_PUBLIC_COOKIE_BLOCK || ""}`;
    //   scriptOneTrustSDK.type = "text/javascript";
    //   scriptOneTrustSDK.id = "one-trust-block";
    //   document.head.appendChild(scriptOneTrustSDK);
    // }
    if (!existingSDKScript) {
      const scriptOneTrustSDK = document.createElement("script");
      scriptOneTrustSDK.src = `${process.env.NEXT_PUBLIC_ONE_TRUST_SCRIPT || ""}?timestamp=${new Date().getTime()}`;
      scriptOneTrustSDK.type = "text/javascript";
      scriptOneTrustSDK.id = "one-trust-script";
      scriptOneTrustSDK.setAttribute(
        "data-domain-script",
        process.env.NEXT_PUBLIC_ONE_TRUST_DATA_DOMAIN || ""
      );
      document.head.appendChild(scriptOneTrustSDK);
    }
  };

  // checking scroll event to trigger create scene popup
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        // Adjust the scroll threshold as needed
        setHasScrolled(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (
      !userSession?.isLoading &&
      userSession?.loggedIn &&
      /^\/recipes\/.+/.test(pathName) &&
      shouldShowPopup("create_scene_suppress") &&
      hasScrolled
    ) {
      dispatch(
        sceneStatesTrigger({
          popupType: "createScenePopup",
          suppression: true,
        })
      );
    }
  }, [userSession, hasScrolled]);

  const getGigyaToken = useGigyaAccountInfo();

  const handleProfileUpdate = ({
    isProfileUpdate,
  }: {
    isProfileUpdate: boolean;
  }) => {
    dispatch(
      setLoginParams({
        uid: "",
        loggedIn: null,
        isLoading: true,
        profile: null,
        id_token: "",
        sobeysCom: null,
        subscribed: false,
      })
    );
    if (isLoaded && gigya && !isHash) {
      gigya.accounts.getAccountInfo({
        callback: async (response: any) => {
          if (response?.errorCode === 0) {
            // global uid addon
            addDataLayer({
              event: "gigyaUID",
              gigyaID: response?.UID,
            });
            setCustomgigyaUid("gigyaUID", response?.UID);

            // one trust addon
            try {
              await oneTrustFunction({ loggedIn: true, uid: response?.UID });
            } catch {
              console.log("failed to load one trust ...");
            }

            const sceneInfo = response?.data?.scene || null;
            if (isProfileUpdate) {
              if (
                userSession?.scene?.cardNumber &&
                !sceneInfo?.cardNumber?.length
              ) {
                flushToken();
                await deleteCookie("add_suppress");
                await deleteCookie("link_suppress");
              } else if (
                userSession?.scene?.cardNumber != sceneInfo?.cardNumber
              ) {
                addDataLayer({
                  event: "card_activation",
                  click_text: "card_activation",
                  click_url: "success",
                  page_title:
                    typeof window != "undefined" ? document?.title : "",
                });
                addDataLayer({
                  event: "scene_registration",
                  click_text: "Add Card",
                  click_url: "step_1",
                  page_title:
                    typeof window != "undefined" ? document?.title : "",
                });
                sendSalesforceInteraction({
                  name: "cardActivation",
                  event: "cardActivation",
                  banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                  isAnonymous: userSession?.uid ? 0 : 1,
                  gigyaUid: userSession?.uid || "",
                  status: "success",
                  cardActivations: "success",
                  utmsource: getUTMParameter("utm_sougce"),
                  utmMedium: getUTMParameter("utm_medium"),
                  utmCampaign: getUTMParameter("utm_campaign"),
                });
                flushToken();
                await deleteCookie("link_suppress");
              }
            }

            let enrollmentStatus, cardInfo;
            if (sceneInfo?.cardNumber) {
              const tokenResponse = await getGigyaToken();
              const checkCard = await checkSceneCard({
                id_token: tokenResponse?.idToken,
              });
              if (checkCard?.code === 439) {
                dispatch(
                  setAlert({
                    message: "Something went wrong!",
                    color: "error",
                    isError: true,
                  })
                );
                dispatch(setLogoutUser(true));
                return;
              }
              enrollmentStatus =
                REVERSE_CARD_STATUS[
                  checkCard?.status as keyof typeof REVERSE_CARD_STATUS
                ];
              enquireEnrolment(response.UID, "success", enrollmentStatus);
              // show popup's
              switch (enrollmentStatus) {
                case "LP00":
                  dispatch(
                    setAlert({
                      message:
                        "Please check your Scene+ Card number. If you are still experiencing issues, contact Scene+ support at 1-866-586-2805.",
                      color: "error",
                      isError: true,
                    })
                  );
                  break;
                case "LP01":
                  if (!sceneInfo?.linkedStatus) {
                    if (
                      shouldShowPopup("link_suppress") &&
                      !["/verify-card", "/scene"].includes(pathName)
                    ) {
                      dispatch(
                        sceneStatesTrigger({
                          popupType: "linkCard",
                          suppression: true,
                        })
                      );
                    }
                  }
                  break;
                case "LP02":
                  dispatch(
                    setAlert({
                      message:
                        "Please check your Scene+ Card number. If you are still experiencing issues, contact Scene+ support at 1-866-586-2805.",
                      color: "error",
                      isError: true,
                    })
                  );
                  if (!sceneInfo?.linkedStatus) {
                    if (
                      shouldShowPopup("link_suppress") &&
                      !["/verify-card", "/scene"].includes(pathName)
                    ) {
                      dispatch(
                        sceneStatesTrigger({
                          popupType: "linkCard",
                          suppression: true,
                        })
                      );
                    }
                  }
                  break;
                case "LP03":
                  if (shouldShowPopup("ghost_suppress")) {
                    dispatch(
                      sceneStatesTrigger({
                        popupType: "ghostCard",
                        suppression: true,
                      })
                    );
                  }
                  break;
                default:
                  break;
              }

              // getting card info
              if (
                sceneInfo?.linkedStatus &&
                ["LP01", "LP02"]?.includes(enrollmentStatus)
              ) {
                const tokenResponse = await getGigyaToken();
                cardInfo = await getLinkedInfo({
                  id_token: tokenResponse?.idToken,
                });
                if (cardInfo?.code === 439) {
                  dispatch(
                    setAlert({
                      message: cardInfo?.message,
                      color: "error",
                      isError: true,
                    })
                  );
                  dispatch(setLogoutUser(true));
                  return;
                }
              }
            } else if (!sceneInfo?.cardNumber) {
              if (
                shouldShowPopup("add_suppress") &&
                !["/verify-card", "/scene", "/my-profile"].includes(pathName)
              ) {
                dispatch(
                  sceneStatesTrigger({
                    popupType: "addCard",
                    suppression: true,
                  })
                );
              }
            }
            dispatch(
              setLoginParams({
                uid: response?.UID,
                isLoading: false,
                loggedIn: true,
                profile: response?.profile,
                scene: { ...sceneInfo, enrollmentStatus, info: cardInfo?.data },
                id_token: response?.id_token,
                lifestyles: response?.data?.lifestyles,
                [process.env.NEXT_PUBLIC_HOME_BANNER as string]:
                  response?.data?.[
                    process.env.NEXT_PUBLIC_HOME_BANNER as string
                  ],
                subscribed:
                  response?.subscriptions?.[
                    process.env.NEXT_PUBLIC_HOME_BANNER as string
                  ]?.eFlyer?.email?.isSubscribed || false,
              })
            );
          } else if (response?.errorCode) {
            //  dispatch(clearShoppingSlice());  //removing temporarily
            addDataLayer({
              event: "gigyaUID",
              gigyaID: "",
            });
            try {
              await oneTrustFunction({ loggedIn: false, uid: null });
            } catch {
              console.log("failed to load one trust ...");
            }
            dispatch(
              setLoginParams({
                uid: "",
                loggedIn: false,
                isLoading: false,
                profile: null,
                id_token: "",
                sobeysCom: null,
                subscribed: false,
              })
            );

            if (
              managementListData.itemsList?.length &&
              managementListData?.isAuthenticated === true
            ) {
              try {
                dispatch(clearShoppingSlice());
              } catch (error) {
                console.log("Failed to clear redux.", error);
              }
            }
          }
        },
      });
    }
  };

  useEffect(() => {
    handleProfileUpdate({ isProfileUpdate: false });
  }, [isLoaded, gigya, pathName]);

  // Handle guide activation event to close the dropdown
  useEffect(() => {
    const handler = () => {
      setClose(false);
    };
    window.addEventListener("guide-activated", handler);
    return () => window.removeEventListener("guide-activated", handler);
  }, []);

  return (
    <>
      {alert && (
        <Toast
          message={
            <span>{RenderHTML(alert?.message)}</span>
          }
          onClose={() => {
            dispatch(setAlert());
          }}
          position="top"
          bgColor={`${alert?.color}`}
          autoCloseDuration={7000}
          closeButton={
            <span className="icon-close text-brandBlack font-bold ml-3 cursor-pointer" />
          }
          className={`${alert?.isError ? "bg-red100 text-brandBlack" : ""} max-w-[935px] lgs:w-full w-[90%]  z-[999] font-normal text-body md:top-[120px] top-[124px] px-4 py-[14px]`}
        />
      )}
      {loader ? (
        <Loader className="fixed inset-0 bg-[#fff] bg-opacity-70 z-[9999] flex items-center justify-center" />
      ) : (
        ""
      )}
        {popupType === "addCard" ? (
          <AddSceneCardPopup
            disableSuppress={!suppression}
            callback={() => {
              flushToken();
              handleProfileUpdate({ isProfileUpdate: false });
              dispatch(setRenderMemo({ renderMemoProfile: true }));
            }}
          />
        ) : (
          <></>
        )}
        {popupType === "createScenePopup" &&
        userSession?.loggedIn &&
        !userSession?.scene?.cardNumber?.length ? (
          <CreateScenePopup />
        ) : (
          <></>
        )}
        {popupType === "linkCard" ? (
          <VerifyPopup disableSuppress={!suppression} />
        ) : (
          <></>
        )}
        {popupType === "ghostCard" ? <CreateAccountPopup /> : <></>}
      <Box
        as="div"
        ref={userDropdownRef}
        className="user-account-details flex items-center z-[9]"
        id="header_info"
        onBlur={(e: any) => {
          const currentTarget = e.currentTarget;
          const relatedTarget = e.relatedTarget;
          if (!currentTarget?.contains(relatedTarget)) {
            setClose(false);
          }
        }}
        onKeyDown={(e: any) => {
          if (e.key === "Enter") {
            e.preventDefault();
            setClose((prev: boolean) => !prev);
          }
        }}
        onClick={() => setClose((prev: boolean) => !prev)}
      >
        <DropdownMenu
          className="dropdown-menu [&_.inputWrapper]:!z-0"
          controlledOpen={close}
          closeOnItemClick={true}
        >
          <DropdownTrigger activeClassName="box-curve lg:bg-brandWhite ">
            <div
              id="points_and_account"
              className="outline-none lg:h-[57px] rounded-lg rounded-b-none  transform transition-all duration-900 ease-in-out w-full flex lg:px-4  items-center 3xs:gap-3 gap-2
        has context menu"
            >
              {userSession?.loggedIn &&
              userSession?.scene?.cardNumber?.length ? (
                <ImageWrapper
                  src="/account-frame.svg"
                  width={24}
                  height={24}
                  alt="Scene Logo"
                />
              ) : (
                <></>
              )}
              {userSession?.scene?.info?.pointBalance ? (
                <Box className="flex gap-3 w-full">
                  <Text
                    as="span"
                    className="text-brandBlack font-bold text-body line-clamp-1 hidden 3xs:block"
                  >
                    {userSession?.scene?.info?.pointBalance + " Pts"}
                  </Text>
                </Box>
              ) : (
                <></>
              )}
              <Box
                as="div"
                className="icon-user bg-brandSecondary text-brandWhite rounded-full 2lg:w-auto lg:w-10 md:w-auto w-10 h-10 px-4 gap-[6px] inline-flex justify-center items-center"
              >
                <Box className="2lg:block lg:hidden md:block hidden max-w-50px">
                  <Text
                    as="span"
                    className="text-brandWhite font-bold whitespace-nowrap text-left"
                  >
                    {userSession?.loggedIn
                      ? formatName(userSession?.profile?.firstName as string)
                      : `Account`}
                  </Text>
                </Box>
              </Box>
            </div>
          </DropdownTrigger>
          <DropdownContent
            className={`lg:!shadow-none !rounded-none !bg-transparent !p-0 left-[inherit] dropdown-outer  4xs:w-[296px] w-full h-auto  mt-0 top-[52px] lg:top-[57px] -z-10 right-0 ${userSession?.loggedIn ? "sm:min-w-[390px] w-full" : ""}`}
          >
            <Box
              as="div"
              className="!bg-brandWhite drop-shadow-custom  rounded-lg rounded-tr-none"
            >
              {userSession?.isLoading ? (
                <Box className="py-4 flex justify-center items-center ">
                  <Loader className="h-full w-8 min-h-[50px]" />
                </Box>
              ) : (
                <>
                  {userSession?.loggedIn ? (
                    <Box as="div">
                      <Box as="div" className="p-6">
                        <Box
                          as="div"
                          className="flex justify-between items-center"
                        >
                          <Text
                            as="span"
                            className="text-base font-bold flex-1 pr-2 line-clamp-1"
                          >
                            {`Hi ${userSession?.profile?.firstName} ${userSession?.profile?.lastName}`}
                          </Text>
                          <Button
                            variant="outline"
                            onKeyDown={(e: any) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleGigyaLogout();
                              }
                            }}
                            aria-label={"Sign Out"}
                            onClick={() => handleGigyaLogout()}
                          >
                            Sign Out
                          </Button>
                        </Box>
                        <Box as="div" className="pt-6">
                          <Text as="ul">
                            <Text as="li" className="mb-4">
                              <Link href="/my-profile" prefetch={false}>Manage Account</Link>
                            </Text>
                            <Text as="li" className="mb-4">
                              <Link href="/preferences-centre" prefetch={false}>
                                Preferences Centre
                              </Link>
                            </Text>
                            <Text as="li" className="mb-4 last:mb-0">
                              <Link href="/scene" prefetch={false}>
                                Scene+ Account
                              </Link>
                            </Text>
                            {/* <Text as="li" className="mb-4 last:mb-0">
                                <Link
                                  onClick={() => setDeleteDialog(true)}
                                  href="#"
                                >
                                  Delete Account
                                </Link>
                              </Text> */}
                          </Text>
                        </Box>
                      </Box>

                      {!userSession?.scene?.cardNumber?.length ? (
                        <Box
                          as="div"
                          className="bg-grey100 p-6 rounded-bl-lg rounded-br-lg "
                        >
                          <Button
                            aria-label="Link Your Scene+ Card"
                            className="w-full"
                            onKeyDown={(e: any) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                dispatch(
                                  sceneStatesTrigger({
                                    popupType: "addCard",
                                    suppression: false,
                                  })
                                );
                              }
                            }}
                            onClick={() => {
                              dispatch(
                                sceneStatesTrigger({
                                  popupType: "addCard",
                                  suppression: false,
                                })
                              );
                            }}
                          >
                            <Text as="span" className="flex gap-2 items-center">
                              <Text className="icon-barcode text-lg"></Text>
                              Link Your Scene+ Card
                            </Text>
                          </Button>
                        </Box>
                      ) : userSession?.scene?.cardNumber?.length &&
                        !userSession?.scene?.linkedStatus ? (
                        ["LP00"].includes(
                          userSession?.scene?.enrollmentStatus
                        ) ? (
                          <></>
                        ) : (
                          <Box as="div" className="bg-grey100 p-6">
                            {["LP03"].includes(
                              userSession?.scene?.enrollmentStatus
                            ) ? (
                              <Button
                                aria-label="Register Scene+ Card"
                                className={`w-full ${["/verify-card"].includes(pathName) ? "hover:text-brandBlack" : ""}`}
                                disabled={["/verify-card"].includes(pathName)}
                              >
                                <Link
                                  href={
                                    process.env
                                      .NEXT_PUBLIC_REGISTER_SCENE_CARD_LINK ||
                                    ""
                                  }
                                  className="icon-third-party flex gap-3 items-center"
                                  target="_blank"
                                >
                                  Register Scene+ Card
                                </Link>
                              </Button>
                            ) : (
                              <Button
                                aria-label="Verify Your Scene+ Card"
                                className={`w-full ${["/verify-card"].includes(pathName) ? "hover:text-brandBlack" : ""}`}
                                disabled={["/verify-card"].includes(pathName)}
                                onKeyDown={async (e: any) => {
                                  if (e.key === "Enter") {
                                    const tokenResponse = await getGigyaToken();
                                    await triggerVerifyCard({
                                      dispatch,
                                      gigya,
                                      setLoading,
                                      router,
                                      pathName,
                                      loadLogin,
                                      callback: async () => {
                                        setIsOpen(false);
                                      },
                                      tokenResponse,
                                    });
                                  }
                                }}
                                onClick={async () => {
                                  const tokenResponse = await getGigyaToken();
                                  await triggerVerifyCard({
                                    dispatch,
                                    gigya,
                                    setLoading,
                                    router,
                                    pathName,
                                    loadLogin,
                                    callback: async () => {
                                      setIsOpen(false);
                                    },
                                    tokenResponse,
                                  });
                                }}
                              >
                                Verify Your Scene+ Card
                              </Button>
                            )}
                          </Box>
                        )
                      ) : userSession?.scene?.info ? (
                        <Box as="div" className="bg-grey100 p-6 ">
                          <div className=" flex justify-between">
                            <Box as="p" className="3xs:text-base text-body">
                              Scene+ Balance:{" "}
                            </Box>
                            <Box className="flex justify-center items-center">
                              <Text
                                as="span"
                                className="3xs:text-base text-body font-bold border-r-[1px] border-solid border-brandBlack mr-2 pr-2"
                              >
                                {userSession?.scene?.info?.dollarValue ||
                                userSession?.scene?.info?.dollarValue == 0
                                  ? "$" + userSession?.scene?.info?.dollarValue
                                  : "--"}
                              </Text>
                              <Text
                                as="span"
                                className="3xs:text-base text-body font-bold  mr-2"
                              >
                                {userSession?.scene?.info?.pointBalance ||
                                userSession?.scene?.info?.pointBalance == 0
                                  ? userSession?.scene?.info?.pointBalance
                                  : "--"}
                              </Text>
                              <Badge className="!font-bold !text-[14px] rounded-tl-[5px] rounded-br-[5px] rounded-tr-none rounded-bl-none !bg-purple300">
                                PTS
                              </Badge>
                            </Box>
                          </div>
                        </Box>
                      ) : (
                        <></>
                      )}
                    </Box>
                  ) : (
                    <Box
                      as="div"
                      className="p-6 flex flex-col gap-3 items-center !bg-brandWhite drop-shadow-custom pt-6  rounded-lg rounded-tr-none"
                    >
                      <Link href="/">
                        <ImageWrapper
                          src="/logo/logo-green.svg"
                          alt="Sobeys"
                          height={25}
                          width={79}
                        />
                      </Link>
                      <Button
                        className=" mt-4 mb-1 px-6 py-[10px]  !text-body !font-normal w-full !border-[1px] !leading-5"
                        variant="primary"
                        onKeyDown={(e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            loadLogin("login");
                          }
                        }}
                        aria-label="Log In"
                        onClick={() => loadLogin("login")}
                      >
                        Log In
                      </Button>
                      <Button
                        className="  !text-body !font-normal w-full !border-[1px] !leading-5 hover:!border-brandSecondary px-6 py-[10px]"
                        variant="outline"
                        onKeyDown={(e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            loadLogin("register");
                          }
                        }}
                        aria-label="Sign Up"
                        onClick={() => loadLogin("register")}
                      >
                        Sign Up
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </DropdownContent>
        </DropdownMenu>
      </Box>
      <input
        type="button"
        className="hidden"
        id="refresh_scene_flow"
        onClick={() => {
          handleProfileUpdate({ isProfileUpdate: true });
        }}
      />
      {/* <DeleteAccountPopup isOpen={deleteDialog} setIsOpen={setDeleteDialog} /> */}
    </>
  );
}
